import { gql, useQuery } from '@apollo/client';
import Button from '@orn/shared-web/components/Button/Button';
import * as React from 'react';

import { formatCalendarDate, formatDateTime } from '../../../utils/formatting';
import AdminSelectUser from '../AdminSelectUser';

import CreateBillModal from './CreateBillModal';
import CreateBillSubmissionModal from './CreateBillSubmissionModal';

import type {
  AdminListBillsQuery,
  AdminListBillsQueryVariables,
  AdminListBillSubmissionsQuery,
  AdminListBillSubmissionsQueryVariables,
} from './__generated__/AdminBillManagmentPane.gql';

const ADMIN_LIST_BILLS = gql`
  query AdminListBills($userId: ID!) {
    adminListBills(userId: $userId) {
      id
      billSubmission {
        id
      }
      createdAt
      updatedAt
      providerName
      amountDue
      dueDate
      status
      source
      description
      accountNumber
      invoiceNumber
      minAmountDue
      statementDate
      serviceStartDate
      serviceEndDate
      isAutopay
      paymentUrl
      lateFeeAmount
      lateFeeDate
      providerPhone
      providerEmail
      payerName
      previousAccountBalance
    }
  }
`;

const ADMIN_LIST_BILL_SUBMISSIONS = gql`
  query AdminListBillSubmissions($userId: ID!) {
    adminListBillSubmissions(userId: $userId) {
      id
      createdAt
      updatedAt
      providerName
      amountDue
      dueDate
      status
      source
      description
      accountNumber
      invoiceNumber
      minAmountDue
      statementDate
      serviceStartDate
      serviceEndDate
      isAutopay
      paymentUrl
      lateFeeAmount
      lateFeeDate
      providerPhone
      providerEmail
      payerName
      previousAccountBalance
    }
  }
`;

const AdminBillManagmentPane = () => {
  const [isCreateBillModalOpen, setIsCreateBillModalOpen] = React.useState(false);
  const [isCreateBillSubmissionModalOpen, setIsCreateBillSubmissionModalOpen] = React.useState(false);
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);
  const [expandedBillIds, setExpandedBillIds] = React.useState<Record<string, boolean>>({});
  const [expandedBillSubmissionIds, setExpandedBillSubmissionIds] = React.useState<Record<string, boolean>>({});

  const { data: billsData, loading: billsLoading } = useQuery<AdminListBillsQuery, AdminListBillsQueryVariables>(
    ADMIN_LIST_BILLS,
    {
      variables: { userId: selectedUserId ?? '' },
      skip: !selectedUserId,
    }
  );

  const { data: billSubmissionsData, loading: billSubmissionsLoading } = useQuery<
    AdminListBillSubmissionsQuery,
    AdminListBillSubmissionsQueryVariables
  >(ADMIN_LIST_BILL_SUBMISSIONS, {
    variables: { userId: selectedUserId ?? '' },
    skip: !selectedUserId,
  });

  const sortedBills = React.useMemo(() => {
    if (!billsData?.adminListBills) {
      return [];
    }
    return [...billsData.adminListBills].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [billsData?.adminListBills]);

  const sortedBillSubmissions = React.useMemo(() => {
    if (!billSubmissionsData?.adminListBillSubmissions) {
      return [];
    }
    return [...billSubmissionsData.adminListBillSubmissions].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [billSubmissionsData?.adminListBillSubmissions]);

  const formatFieldValue = (value: unknown): string => {
    if (value == null || value === '') {
      return 'No value';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'object' && 'year' in value && 'month' in value && 'day' in value) {
      return formatCalendarDate(value as Parameters<typeof formatCalendarDate>[0], 'long');
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'bigint') {
      return `${value}`;
    }
    return 'No value';
  };

  return (
    <div className="flex h-full w-full flex-col gap-6 p-6">
      <div className="flex flex-col gap-4">
        <div className="text-3xl font-bold">Bill managment</div>
        <div className="text-secondary text-sm">Create bill records and bill submission records for users.</div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button onClick={() => setIsCreateBillModalOpen(true)}>Create bill</Button>
        <Button color="secondary" onClick={() => setIsCreateBillSubmissionModalOpen(true)}>
          Create bill submission
        </Button>
      </div>

      <div className="w-full max-w-md">
        <AdminSelectUser selectedUserId={selectedUserId} onSelectionChange={setSelectedUserId} />
      </div>

      {!selectedUserId ? (
        <div className="text-secondary text-sm">Select user</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="border-primary bg-primary rounded-lg border p-4">
            <div className="mb-3 text-lg font-semibold">Bills</div>
            {billsLoading ? (
              <div className="text-secondary text-sm">Loading bills...</div>
            ) : sortedBills.length ? (
              <div className="flex flex-col gap-3">
                {sortedBills.map((bill) => (
                  <div key={bill.id} className="bg-secondary border-primary rounded-lg border p-3">
                    {(() => {
                      const isExpanded = Boolean(expandedBillIds[bill.id]);
                      return (
                        <>
                          <div className="text-sm font-semibold">
                            {bill.providerName || 'No provider'} • {bill.status}
                          </div>
                          <div className="text-tertiary text-xs">ID: {bill.id}</div>
                          <div className="text-tertiary text-xs">
                            Bill submission ID: {bill.billSubmission?.id || 'No bill submission'}
                          </div>
                          <div className="text-tertiary text-xs">Amount: {bill.amountDue || 'No amount'}</div>
                          <div className="text-tertiary text-xs">
                            Due date: {formatCalendarDate(bill.dueDate, 'long')}
                          </div>
                          <div className="text-tertiary text-xs">Source: {bill.source}</div>
                          <div className="text-tertiary text-xs">Created at: {formatDateTime(bill.createdAt)}</div>
                          {isExpanded && (
                            <>
                              <div className="text-tertiary text-xs">Updated at: {formatDateTime(bill.updatedAt)}</div>
                              <div className="text-tertiary text-xs">
                                Description: {formatFieldValue(bill.description)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Account number: {formatFieldValue(bill.accountNumber)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Invoice number: {formatFieldValue(bill.invoiceNumber)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Min amount due: {formatFieldValue(bill.minAmountDue)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Statement date: {formatFieldValue(bill.statementDate)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Service start date: {formatFieldValue(bill.serviceStartDate)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Service end date: {formatFieldValue(bill.serviceEndDate)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Is autopay: {formatFieldValue(bill.isAutopay)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Payment URL: {formatFieldValue(bill.paymentUrl)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Late fee amount: {formatFieldValue(bill.lateFeeAmount)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Late fee date: {formatFieldValue(bill.lateFeeDate)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Provider phone: {formatFieldValue(bill.providerPhone)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Provider email: {formatFieldValue(bill.providerEmail)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Payer name: {formatFieldValue(bill.payerName)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Previous account balance: {formatFieldValue(bill.previousAccountBalance)}
                              </div>
                            </>
                          )}
                          {!isExpanded && bill.description && (
                            <div className="text-secondary mt-1 text-sm">{bill.description}</div>
                          )}
                          <button
                            type="button"
                            className="text-brand mt-2 text-xs font-medium"
                            onClick={() => setExpandedBillIds((prev) => ({ ...prev, [bill.id]: !prev[bill.id] }))}
                          >
                            {isExpanded ? 'Show less' : 'Show all fields'}
                          </button>
                        </>
                      );
                    })()}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-secondary text-sm">No bills found</div>
            )}
          </div>

          <div className="border-primary bg-primary rounded-lg border p-4">
            <div className="mb-3 text-lg font-semibold">Bill submissions</div>
            {billSubmissionsLoading ? (
              <div className="text-secondary text-sm">Loading bill submissions...</div>
            ) : sortedBillSubmissions.length ? (
              <div className="flex flex-col gap-3">
                {sortedBillSubmissions.map((billSubmission) => (
                  <div key={billSubmission.id} className="bg-secondary border-primary rounded-lg border p-3">
                    {(() => {
                      const isExpanded = Boolean(expandedBillSubmissionIds[billSubmission.id]);
                      return (
                        <>
                          <div className="text-sm font-semibold">
                            {billSubmission.providerName || 'No provider'} • {billSubmission.status}
                          </div>
                          <div className="text-tertiary text-xs">ID: {billSubmission.id}</div>
                          <div className="text-tertiary text-xs">Amount: {billSubmission.amountDue || 'No amount'}</div>
                          <div className="text-tertiary text-xs">
                            Due date:{' '}
                            {billSubmission.dueDate
                              ? formatCalendarDate(billSubmission.dueDate, 'long')
                              : 'No due date'}
                          </div>
                          <div className="text-tertiary text-xs">Source: {billSubmission.source}</div>
                          <div className="text-tertiary text-xs">
                            Created at: {formatDateTime(billSubmission.createdAt)}
                          </div>
                          {isExpanded && (
                            <>
                              <div className="text-tertiary text-xs">
                                Updated at: {formatDateTime(billSubmission.updatedAt)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Description: {formatFieldValue(billSubmission.description)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Account number: {formatFieldValue(billSubmission.accountNumber)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Invoice number: {formatFieldValue(billSubmission.invoiceNumber)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Min amount due: {formatFieldValue(billSubmission.minAmountDue)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Statement date: {formatFieldValue(billSubmission.statementDate)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Service start date: {formatFieldValue(billSubmission.serviceStartDate)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Service end date: {formatFieldValue(billSubmission.serviceEndDate)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Is autopay: {formatFieldValue(billSubmission.isAutopay)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Payment URL: {formatFieldValue(billSubmission.paymentUrl)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Late fee amount: {formatFieldValue(billSubmission.lateFeeAmount)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Late fee date: {formatFieldValue(billSubmission.lateFeeDate)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Provider phone: {formatFieldValue(billSubmission.providerPhone)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Provider email: {formatFieldValue(billSubmission.providerEmail)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Payer name: {formatFieldValue(billSubmission.payerName)}
                              </div>
                              <div className="text-tertiary text-xs">
                                Previous account balance: {formatFieldValue(billSubmission.previousAccountBalance)}
                              </div>
                            </>
                          )}
                          {!isExpanded && billSubmission.description && (
                            <div className="text-secondary mt-1 text-sm">{billSubmission.description}</div>
                          )}
                          <button
                            type="button"
                            className="text-brand mt-2 text-xs font-medium"
                            onClick={() =>
                              setExpandedBillSubmissionIds((prev) => ({
                                ...prev,
                                [billSubmission.id]: !prev[billSubmission.id],
                              }))
                            }
                          >
                            {isExpanded ? 'Show less' : 'Show all fields'}
                          </button>
                        </>
                      );
                    })()}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-secondary text-sm">No bill submissions found</div>
            )}
          </div>
        </div>
      )}

      <CreateBillModal isOpen={isCreateBillModalOpen} onOpenChange={setIsCreateBillModalOpen} />
      <CreateBillSubmissionModal
        isOpen={isCreateBillSubmissionModalOpen}
        onOpenChange={setIsCreateBillSubmissionModalOpen}
      />
    </div>
  );
};

export default AdminBillManagmentPane;
